import {CommonModule} from '@angular/common';
import {Component, ChangeDetectionStrategy, inject} from '@angular/core';
import {RouterOutlet, ActivatedRoute, Router, NavigationEnd} from '@angular/router';
import {LoadComponent} from '@brunata-angular/ui';
import {
    TranslatePipe,
    filterNulls,
    composeComparators,
    keyComparator,
    TranslateService,
} from '@brunata-angular/util';
import {Store} from '@ngrx/store';
import {map, Observable, filter, startWith, combineLatest, switchMap, BehaviorSubject} from 'rxjs';
import {selectBuilding} from 'src/app/state/selectors';
import {VariableAlarmsCardViewComponent} from './variable-alarms-card-view/variable-alarms-card-view.component';
import {VariableAlarmsTableComponent} from './variable-alarms-table/variable-alarms-table.component';
import {VariableAlarmsService} from './variable-alarms.service';
import {VariableAlarmSubmit, VariableAlarmWithTranslations} from './variable-alarm.model';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {SelectTemplateOrCustomVariableAlarmComponent} from './create-variable-alarm/select-template-or-custom-variable-alarm/select-template-or-custom-variable-alarm.component';

@Component({
    selector: 'app-variable-alarms-overview',
    standalone: true,
    imports: [
        CommonModule,
        VariableAlarmsCardViewComponent,
        VariableAlarmsTableComponent,
        RouterOutlet,
        TranslatePipe,
        LoadComponent,
    ],
    templateUrl: './variable-alarms-overview.component.html',
    styleUrl: './variable-alarms-overview.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariableAlarmsOverviewComponent {
    private readonly store = inject(Store);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly variableAlarmService = inject(VariableAlarmsService);
    private readonly translateService = inject(TranslateService);
    private readonly toastrService = inject(ToastrService);
    private readonly modalService = inject(NgbModal);

    private confirmationDialogService = inject(ConfirmationDialogService);
    private readonly updateViewSubject$: BehaviorSubject<string> = new BehaviorSubject('');
    private readonly buildingNo$ = this.store.select(selectBuilding).pipe(
        map(state => (state.status === 'success' ? state.buildingNo : null)),
        filterNulls(),
    );
    viewMode: 'card' | 'table' = 'card';

    protected readonly view$: Observable<{
        buildingNo: string;
        variableAlarms: VariableAlarmWithTranslations[];
    }> = combineLatest([this.buildingNo$, this.updateViewSubject$.pipe(startWith(''))]).pipe(
        switchMap(([buildingNo]) =>
            combineLatest([
                this.variableAlarmService.getVariableAlarms(buildingNo),
                this.variableAlarmService.sorting.pipe(filterNulls()),
            ]).pipe(
                map(([variableAlarms, sorting]) => ({
                    buildingNo: buildingNo,
                    variableAlarms: variableAlarms
                        .map(variableAlarm => {
                            const allocationUnit = this.translateService.translate(
                                'mappers_allocationUnitMap.' + variableAlarm.allocationUnit,
                                'online',
                                'common',
                            );

                            const unit = this.translateService.translate(
                                'mappers_measurementUnit.' + variableAlarm.unit,
                                'online',
                                'common',
                            );
                            return {
                                ...variableAlarm,
                                usageTranslated: allocationUnit,
                                unitTranslated: variableAlarm.unit === null ? null : unit,
                            };
                        })
                        .sort(
                            composeComparators([
                                keyComparator(
                                    variableAlarm =>
                                        variableAlarm[
                                            this.viewMode === 'card'
                                                ? 'alarmName'
                                                : (sorting.column as keyof VariableAlarmWithTranslations)
                                        ],
                                    this.viewMode === 'card' ? 'asc' : sorting.direction,
                                ),
                            ]),
                        ),
                })),
            ),
        ),
    );

    protected readonly noActivatedChildren$: Observable<boolean> = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.activatedRoute.children.length === 0),
    );
    constructor() {
        switch (localStorage.getItem(`online.monitoring.variable-alarms.overview.view-mode`)) {
            case 'card':
                this.setViewMode('card');
                break;
            default:
                this.setViewMode('table');
        }
    }
    deleteVariableAlarm(
        buildingNo: string,
        variableAlarmWithTranslations: VariableAlarmWithTranslations,
    ): void {
        void this.confirmationDialogService
            .confirm(
                this.translateService.translate('common_warning', 'online', 'common'),
                this.translateService
                    .translate('monitoring_alarm_delete_warning')
                    .replace('{0}', variableAlarmWithTranslations.alarmName ?? ''),
            )
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.variableAlarmService
                    .deleteVariableAlarm(buildingNo, variableAlarmWithTranslations)
                    .subscribe(() => {
                        this.updateViewSubject$.next('');
                        this.toastrService.success(
                            this.translateService.translate('variable-alarms_delete_success'),
                        );
                    });
            });
    }

    protected editVariableAlarm(deviationNotificRuleId: string | null): void {
        if (deviationNotificRuleId === null) {
            return;
        }

        void this.router.navigate([deviationNotificRuleId, 'edit'], {
            relativeTo: this.activatedRoute,
        });
    }
    protected duplicateVariableAlarm(
        buildingNo: string,
        variableAlarmWithTranslations: VariableAlarmWithTranslations | null,
    ): void {
        if (variableAlarmWithTranslations === null) {
            return;
        }
        const variableAlarmToSubmit: VariableAlarmSubmit = {
            allocationUnit: variableAlarmWithTranslations.allocationUnit,
            unit:
                variableAlarmWithTranslations.unit !== null &&
                variableAlarmWithTranslations.unit !== ''
                    ? Number(variableAlarmWithTranslations.unit)
                    : null,
            deviationType: variableAlarmWithTranslations.deviationType,
            deviationValue: variableAlarmWithTranslations.deviationValue,
            minimumDeviationPeriod: variableAlarmWithTranslations.minimumDeviationPeriod ?? null,
            activeStartTime: variableAlarmWithTranslations.activeStartTime ?? null,
            activeEndTime: variableAlarmWithTranslations.activeEndTime ?? null,
            throttle: variableAlarmWithTranslations.throttle ?? null,
            alarmName: `${variableAlarmWithTranslations.alarmName} - ${this.translateService.translate('utilities_Copy_key')}`,
            recipients: variableAlarmWithTranslations.recipients.map(recipient => recipient ?? ''),
            meterIds: variableAlarmWithTranslations.meterIds,
        };
        this.variableAlarmService
            .createVariableAlarm(buildingNo, {
                ...variableAlarmToSubmit,
            })
            .subscribe(result => {
                this.editVariableAlarm(result);
                const successMessage = this.translateService
                    .translate('variable-alarms_created')
                    .replace(
                        '{0}',
                        `${variableAlarmWithTranslations.alarmName} - ${this.translateService.translate('utilities_Copy_key')}`,
                    );

                this.toastrService.success(successMessage);
            });
    }
    protected setViewMode(view: 'table' | 'card'): void {
        this.viewMode = view;
        localStorage.setItem(`online.monitoring.variable-alarms.overview.view-mode`, view);
    }
    protected async createVariableAlarm(): Promise<void> {
        const modalRef = this.modalService.open(SelectTemplateOrCustomVariableAlarmComponent, {
            size: 'xl',
            backdrop: 'static',
        });
        await modalRef.result
            .then((template: {type: string; id: string} | null | undefined) => {
                if (template == null) {
                    return;
                }
                void this.router.navigate(['create', template.type], {
                    relativeTo: this.activatedRoute,
                    queryParams: {templateId: template.id},
                });
            })
            .catch(() => {
                // Modal dismissed (Esc / backdrop) without selecting a template — no-op.
            });
    }
}
